import React from 'react';
import { useQuery } from '@apollo/client';
import { MultiselectField } from 'components';
import { H5 } from 'components/typography';
import CardContainer from 'components/cards/CardContainer';
import styles from './styles.scss';
import { useTranslation } from 'react-i18next';

import { getChannelGroupRoles } from 'lane-shared/graphql/query';

type Props = {
  channelId: string;
  groupRoles: { label: string; value: string }[];
  onChange: (items: any[]) => void;
  defaultSelectionGroupRoleNames: string[];
  errorMessage: string | null;
};

export const TeamMultiSelect = ({
  channelId,
  groupRoles,
  onChange,
  errorMessage,
  defaultSelectionGroupRoleNames = [],
}: Props) => {
  const { t } = useTranslation();

  const { data, loading } = useQuery(getChannelGroupRoles, {
    variables: {
      id: channelId,
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const groupRolesList = data?.channel?.groupRoles ?? [];
      const defaultGroupRoles = groupRolesList.filter(
        x => x && defaultSelectionGroupRoleNames.includes(x.name)
      );

      // Set initial values based on passed defaults
      onChange(defaultGroupRoles.map(mapGroupRoleToOption));
    },
  });

  const groupRolesList = data?.channel?.groupRoles ?? [];
  const groupRoleOptions = groupRolesList
    .filter(
      (groupRole): groupRole is NonNullable<typeof groupRole> =>
        groupRole !== null && groupRole !== undefined
    )
    .map(mapGroupRoleToOption);

  return (
    <CardContainer className={styles.bulkSettingsInnerCard}>
      <div className={styles.teamMultiSelect}>
        <H5>
          {t(
            'web.admin.channel.teamManagement.team.BulkSendInviteV2.bulkSettings.teamMultiSelect.header'
          )}
        </H5>
        <MultiselectField
          label={t(
            'web.admin.channel.teamManagement.team.BulkSendInviteV2.bulkSettings.teamMultiSelect.label'
          )}
          fixedLabel
          isRequired
          isFullWidth
          value={groupRoles}
          items={groupRoleOptions}
          onChange={value => onChange(value)}
          doTranslation={false}
          testId="bulkInviteTeamMultiSelect"
          errors={errorMessage ? [errorMessage] : null}
          isLoading={loading}
          truncateSelectedItems
        />
      </div>
    </CardContainer>
  );
};

const mapGroupRoleToOption = (groupRole: {
  name: string;
  _id: string;
}): { label: string; value: string } => {
  return {
    label: groupRole.name,
    value: groupRole._id,
  };
};
